import { useTranslation } from 'next-i18next';
import { useState } from 'react';
import {
  ApiJobTitleAutocompleteOk, ApiLocationTooltipOk, fetcher, isApiV1Errors,
} from '@utils/data-fetching';
import { PlusIcon } from '@heroicons/react/20/solid';
import { CheckIcon, MagnifyingGlassIcon } from '@heroicons/react/24/solid';
import { bjToast, Button } from '@components/common';
import { ComboBox } from '@components/common/Forms';
import {
  ApiUserPreferencesInformationsOk,
  UserPreferenceJobTitle,
} from '@type/v1-api-types/user-subscription';

// Interface
// *****************************************************
interface ApplicationSummaryEditAutomatedJobsModalProps {
  informations: ApiUserPreferencesInformationsOk,
  locale: Locale,
  onClose: () => void,
  onDeactivate: () => void,
}

/**
 * @description Internal Application Summary component.
 */
export const ApplicationSummaryEditAutomatedJobsModal = (props : ApplicationSummaryEditAutomatedJobsModalProps) => {
  const {
    informations,
    locale,
    onClose,
    onDeactivate,
  } = props;

  const { t } = useTranslation('common');

  const [addNewJobTitle, setVisibleAddNewJobTitle] = useState(false);
  const [addNewLocation, setAddNewLocation] = useState(false);

  const [jobTitles, setJobTitles] = useState<UserPreferenceJobTitle[]>(() => {
    const allJobTitles = [...informations.jobTitles, ...informations.suggestedData.jobTitles];
    const uniqueJobTitles = allJobTitles.filter((title, index) => allJobTitles.findIndex((term) => term.name === title.name) === index);
    return uniqueJobTitles;
  });

  // State for selected job titles
  const [selectedJobTitles, setSelectedJobTitles] = useState(informations.jobTitles);
  // Function to add or remove selected job title
  const toggleSelectedJobTitle = (jobTitle: UserPreferenceJobTitle) => {
    const index = selectedJobTitles.findIndex((title) => title.name === jobTitle.name);
    if (index === -1) {
      setSelectedJobTitles((prevTitles) => [...prevTitles, jobTitle]);
    } else {
      setSelectedJobTitles((prevTitles) => prevTitles.filter((title) => title.name !== jobTitle.name));
    }
  };

  const addNewJobTitleToInputs = (jobTitle: UserPreferenceJobTitle) => {
    const index = selectedJobTitles.findIndex((title) => title.name === jobTitle.name);
    if (index === -1) {
      setSelectedJobTitles((prevTitles) => [...prevTitles, jobTitle]);
      setJobTitles((prevTitles) => [...prevTitles, jobTitle]);
    }
  };


  const [selectedLocations, setSelectedLocations] = useState(informations.locationList);

  const [locations, setLocations] = useState(() => {
    const allLocations = [...informations.locationList, ...informations.suggestedData.locationList];
    const uniqueLocations = allLocations.filter((loc, index) => allLocations.indexOf(loc) === index);
    return uniqueLocations;
  });

  const toggleSelectedLocation = (location:string) => {
    setSelectedLocations((prevLocations) => {
      if (!prevLocations.includes(location)) {
        return [...prevLocations, location];
      }
      return prevLocations.filter((loc) => loc !== location);
    });
  };

  const addNewLocationToInputs = (newLocation: string) => {
    setSelectedLocations((prevLocations) => {
      if (!prevLocations.includes(newLocation)) {
        return [...prevLocations, newLocation];
      }
      return prevLocations;
    });

    setLocations((prevLocations) => {
      if (!prevLocations.includes(newLocation)) {
        return [...prevLocations, newLocation];
      }
      return prevLocations;
    });
  };

  const saveInformations = () => {
    const payload = {
      jobTitles: selectedJobTitles,
      locationList: selectedLocations,
    };

    fetcher('/v1/auto-apply', locale, {
      method: 'POST',
      payload,
      stringifyPayload: true,
    }).then((response) => {
      if (isApiV1Errors(response)) {
        bjToast.error(response.errors[0].message);
      } else if (response) {
        bjToast.success(t('premium.talent.applicationSummary.modal.saved'));
        onClose();
      }
    }).catch(() => {
    });
  };

  return (

    <>
      <div className="p-4">
        <p className="mb-5 text-md font-bold">
          {t('premium.talent.applicationSummary.modal.heading')}
        </p>
        <div className="pb-10">
          <h3 className="mb-4 font-bold">
            {t('premium.talent.applicationSummary.modal.followed.jobs')}
          </h3>
          <div className="flex flex-wrap items-center">
            {jobTitles.map((jobTitle:UserPreferenceJobTitle) => (
              <div
                role="presentation"
                key={jobTitle.id}
                className={`${selectedJobTitles.some((selectedTitle) => selectedTitle.name === jobTitle.name) ? 'bg-dark text-white' : 'text-ink'} mb-2 mr-2 flex w-fit cursor-pointer items-center rounded-full border px-4 py-2 text-sm`}
                onClick={() => toggleSelectedJobTitle(jobTitle)}
              >
                {!selectedJobTitles.some((selectedTitle) => selectedTitle.name === jobTitle.name) && <PlusIcon className="mr-2 size-5 text-ink" />}
                {selectedJobTitles.some((selectedTitle) => selectedTitle.name === jobTitle.name) && <CheckIcon className="mr-2 size-5 text-white" />}
                {jobTitle.name}
              </div>
            ))}

            <div role="presentation" onClick={() => setVisibleAddNewJobTitle(!addNewJobTitle)} className="mb-2 mr-2 flex w-fit cursor-pointer items-center rounded-full bg-secondary/20 px-4 py-2 text-sm text-secondary">
              <MagnifyingGlassIcon className="mr-2 size-5 text-secondary" />
              {t('premium.talent.applicationSummary.modal.job.add')}
            </div>
          </div>
          <ComboBox
            id="job-title-add"
            placeholder={t('premium.talent.applicationSummary.modal.jobs.placeholder')}
            buttonsContainerClass="top-2.5 right-0"
            className={`${addNewJobTitle ? 'block' : 'hidden'} rounded-md`}
            minLength={3}
            options={{
              showDropdownButton: true,
              showResetButton: false,
              valueFromKey: 'name',
              clearOnSelect: true,
              apiList: {
                fetchUrl: '/job-title-autocomplete',
                queryParams: (query: string) => ({ term: query }),
              },
            }}
            onSelectedItemChange={(item) => {
              const job = item as ApiJobTitleAutocompleteOk;
              if (job) addNewJobTitleToInputs(job);
            }}
            renderList={(item, index, listKey, getItemProps, listStyling, highlightedIndex) => {
              // add a type to make TS behave nicely; we already checked for errors object inside Combobox fetch
              const job = item as ApiJobTitleAutocompleteOk;
              return (
                <li key={listKey} className={`${listStyling} flex-col`} {...getItemProps({ item, index })}>
                  <span>{job.name}</span>
                  <span className={`${highlightedIndex === index ? 'text-white' : 'text-ink-medium'} -mt-0.5 text-tiny font-normal transition-all duration-150`}>
                    { // we might have multiple domains for a job name, spread them nicely
                          job.domains && (job.domains).map((domain, i) => {
                            if (i === 0) return (domain.name);
                            return (`, ${domain.name}`);
                          })
                      }
                  </span>
                </li>
              );
            }}
          />
        </div>


        <div className="border-b pb-10">
          <h3 className="mb-4 font-bold">
            {t('premium.talent.applicationSummary.modal.followed.location')}
          </h3>
          <div className="flex flex-wrap items-center">
            {locations.map((location) => (
              <div
                role="presentation"
                key={location}
                className={`${selectedLocations.includes(location) ? 'bg-dark text-white' : 'text-ink'} mb-2 mr-2 flex w-fit cursor-pointer items-center rounded-full border px-4 py-2 text-sm`}
                onClick={() => toggleSelectedLocation(location)}
              >

                {!selectedLocations.includes(location) && <PlusIcon className="mr-2 size-5 text-ink" />}
                {selectedLocations.includes(location) && <CheckIcon className="mr-2 size-5 text-white" />}
                {location}
              </div>
            ))}
            <div role="presentation" onClick={() => setAddNewLocation(!addNewLocation)} className="mb-2 mr-2 flex w-fit cursor-pointer items-center rounded-full bg-secondary/20 px-4 py-2 text-sm text-secondary">
              <MagnifyingGlassIcon className="mr-2 size-5 text-secondary" />
              {t('premium.talent.applicationSummary.modal.location.add')}
            </div>
          </div>
          <ComboBox
            id="job-location-add"
            placeholder={t('premium.talent.applicationSummary.modal.location.placeholder')}
            buttonsContainerClass="top-2.5 right-0"
            className={`${addNewLocation ? 'block' : 'hidden'} rounded-md`}
            minLength={3}
            options={{
              showDropdownButton: true,
              showResetButton: false,
              valueFromKey: 'name',
              apiList: {
                fetchUrl: '/v1/location-tooltip',
                queryParams: (query: string) => ({ location: query }),
              },
            }}
            onSelectedItemChange={(item) => {
              const location = item as ApiLocationTooltipOk;
              if (location) addNewLocationToInputs(location.locality);
            }}
            renderList={(item, index, listKey, getItemProps, listStyling, highlightedIndex) => {
              // add a type to make TS behave nicely; we already checked for errors object inside Combobox fetch
              const location = item as ApiLocationTooltipOk;
              return (
                <li key={listKey} className={`${listStyling} flex-col`} {...getItemProps({ item, index })}>
                  <span className={`${highlightedIndex === index ? 'text-white' : 'text-ink-medium'} -mt-0.5 text-tiny font-normal transition-all duration-150`}>
                    {location.country}
                  </span>
                  <span>{location.locality}</span>
                </li>
              );
            }}
          />
        </div>

        <div className="flex flex-col-reverse items-center justify-between p-4 md:flex-row">
          <Button
            styling="none"
            aria-label={t('global.btn.close')}
            title={t('global.btn.close')}
            onClick={onDeactivate}
            className="block md:inline-block"
          >
            {t('premium.talent.applicationSummary.modal.deny.text')}
          </Button>

          <Button
            type="button"
            styling="solid"
            color="secondary"
            rounding="full"
            className="mb-5 block w-full md:mb-0 md:inline-block md:w-fit"
            onClick={saveInformations}
          >
            {t('premium.talent.applicationSummary.modal.save.text')}
          </Button>

        </div>
      </div>
    </>
  );
};

